import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/dates";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChevronDown, ChevronUp, Check, Minus, X, CircleDot } from "lucide-react";

const RAG = { green: "bg-rag-green text-white", amber: "bg-amber-500 text-white", red: "bg-rag-red text-white" };
const ANSWER = { yes: [Check, "text-rag-green", "Yes"], partially: [CircleDot, "text-amber-500", "Partially"], unsure: [Minus, "text-muted-foreground", "Not sure"], no: [X, "text-rag-red", "No"] };

const BAR_COLOUR = { green: "bg-rag-green", amber: "bg-amber-400", red: "bg-coral" };
const ragOfScore = (pct) => { if (pct >= 80) return "green"; if (pct >= 50) return "amber"; return "red"; };

function FollowUpStatus({ check }) {
  if (check.signed_up) return <Badge variant="outline" className="border-rag-green text-rag-green">Signed up</Badge>;
  if (check.followed_up_at) return <span className="text-muted-foreground">Follow-up sent {fmtDate(check.followed_up_at)}</span>;
  if (check.rag === "green") return <span className="text-muted-foreground">No follow-up needed</span>;
  return <span className="text-amber-600 font-medium">Follow-up pending</span>;
}

function AnswerRow({ q, cat, value }) {
  const [Icon, cls, label] = ANSWER[value] || ANSWER.no;
  return (
    <li className="flex items-start gap-2 text-sm"><Icon className={`h-4 w-4 mt-0.5 shrink-0 ${cls}`} /><span className="text-brand flex-1"><span className="text-xs text-muted-foreground uppercase tracking-wide mr-2">{cat}</span>{q}</span><span className={`text-xs font-semibold ${cls}`}>{label}</span></li>
  );
}

export default function SelfChecksAdmin() {
  const [data, setData] = useState({ questions: [], checks: [], insights: [] });
  const [filter, setFilter] = useState("all");
  const [openId, setOpenId] = useState(null);
  useEffect(() => { api.get("/admin/self-checks").then((r) => setData(r.data)).catch(() => {}); }, []);

  const rows = data.checks.filter((c) => filter === "all" || c.rag === filter);
  const counts = data.checks.reduce((a, c) => ({ ...a, [c.rag]: (a[c.rag] || 0) + 1 }), {});

  return (
    <div>
      {data.insights?.length > 0 && (
        <div className="bg-brand text-white rounded-2xl p-6 mb-6" data-testid="self-check-insights">
          <div className="label-caps text-coral">Webinar talking points</div>
          <h3 className="font-heading font-bold text-xl mt-1">Where care homes score lowest</h3>
          <p className="text-sm text-white/70 mt-1">Average evidence score per assessment section across all {data.checks.length} completed assessments — weakest first.</p>
          <ul className="mt-5 space-y-3">
            {data.insights.map((s, i) => (
              <li key={s.section} data-testid={`insight-${s.section}`} className="grid grid-cols-[1.5rem_1fr_auto] items-center gap-3">
                <span className="text-white/50 font-heading font-bold">{i + 1}</span>
                <div>
                  <div className="flex items-center justify-between text-sm"><span className="font-semibold">{s.title}</span><span className="text-white/70 text-xs ml-3">{s.weak_homes} of {s.homes} homes answered No / Not sure</span></div>
                  <div className="h-2 bg-white/15 rounded-full mt-1.5 overflow-hidden"><div className={`h-full rounded-full ${BAR_COLOUR[ragOfScore(s.avg_pct)]}`} style={{ width: `${s.avg_pct}%` }} /></div>
                </div>
                <span className="font-heading font-bold text-lg w-14 text-right">{s.avg_pct}%</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="self-check-filters">
        {["all", "red", "amber", "green"].map((f) => (
          <button key={f} data-testid={`self-check-filter-${f}`} onClick={() => setFilter(f)}
            className={`rounded-full px-4 h-8 text-sm font-medium border transition-colors ${filter === f ? "bg-brand text-white border-brand" : "border-cream-deep text-brand hover:bg-cream"}`}>
            {f === "all" ? `All (${data.checks.length})` : `${f[0].toUpperCase() + f.slice(1)} (${counts[f] || 0})`}
          </button>
        ))}
        <span className="text-xs text-muted-foreground ml-auto">Amber/Red leads get an automatic follow-up email 3 days after their check.</span>
      </div>
      <div className="bg-cream-card border border-cream-deep rounded-xl overflow-hidden">
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Date</TableHead><TableHead>Name</TableHead><TableHead>Care home</TableHead><TableHead>Score</TableHead><TableHead>Status</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {rows.map((c, i) => (
              <React.Fragment key={c.id}>
                <TableRow data-testid={`self-check-row-${i}`} className={`cursor-pointer ${i % 2 ? "bg-cream/50" : ""}`} onClick={() => setOpenId(openId === c.id ? null : c.id)}>
                  <TableCell className="text-muted-foreground whitespace-nowrap">{fmtDate(c.created_at)}</TableCell>
                  <TableCell><div className="font-medium">{c.name}</div><a href={`mailto:${c.email}`} onClick={(e) => e.stopPropagation()} className="text-xs text-muted-foreground hover:text-coral">{c.email}</a></TableCell>
                  <TableCell>{c.care_home_name}</TableCell>
                  <TableCell><Badge data-testid={`self-check-rag-${i}`} className={`${RAG[c.rag]} rounded-full`}>{c.score}% · {c.rag}</Badge></TableCell>
                  <TableCell className="text-xs">
                    <FollowUpStatus check={c} />
                  </TableCell>
                  <TableCell className="text-right">{openId === c.id ? <ChevronUp className="h-4 w-4 inline" /> : <ChevronDown className="h-4 w-4 inline" />}</TableCell>
                </TableRow>
                {openId === c.id && (
                  <TableRow className="hover:bg-transparent"><TableCell colSpan={6} className="bg-cream/60">
                    <ul className="space-y-2 py-2 max-w-3xl" data-testid={`self-check-answers-${i}`}>
                      {data.questions.map((q) => <AnswerRow key={q.id} q={q.q} cat={q.cat} value={c.answers?.[q.id]} />)}
                    </ul>
                  </TableCell></TableRow>
                )}
              </React.Fragment>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">No self-checks yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
